import {
	FORMULA_OPERATIONS as FP,
	GeometryTypes,
	IReferenceDimension,
	PathOperationTypes,
	ShapeType,
} from "../widgets/interfaces/model/enumerations/index";
import { IPresetGeometry } from "../widgets/interfaces/model/geometry/PresetGeometry";

/**
 * star5 preset definition
 * ECMA-376, Third Edition, Part 1 - Fundamentals And Markup Language Reference
 * OfficeOpenXML-DrawingMLGeometries/presetShapeDefinitions.xml
 */

export const star5: IPresetGeometry = {
	ahLst: [
		{ gdRefY: "adj", maxY: 50000, minY: 0, pos: { x: "hc", y: "yAdj" }
			, referenceDimension: IReferenceDimension.CUSTOM },
	],
	avList: [
		{ name: "adj", fmla: { operation: FP.LiteralValue, operands: [19098] } },
		{ name: "hf", fmla: { operation: FP.LiteralValue, operands: [105146] } },
		{ name: "vf", fmla: { operation: FP.LiteralValue, operands: [110557] } },
	],

	gdLst: [
		{ name: "a", fmla: { operation: FP.PinTo, operands: [0, "adj", 50000] } },
		{ name: "swd2", fmla: { operation: FP.MultiplyDivide, operands: ["wd2", "hf", 100000] } },
		{ name: "shd2", fmla: { operation: FP.MultiplyDivide, operands: ["hd2", "vf", 100000] } },
		{ name: "svc", fmla: { operation: FP.MultiplyDivide, operands: ["vc", "vf", 100000] } },
		// { name: "dx1", fmla: { operation: FP.Cosine, operands: ["swd2", 1080000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "dx1", fmla: { operation: FP.Cosine, operands: ["swd2", 18] } },
		// { name: "dx2", fmla: { operation: FP.Cosine, operands: ["swd2", 18360000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "dx2", fmla: { operation: FP.Cosine, operands: ["swd2", 306] } },
		// { name: "dy1", fmla: { operation: FP.Sine, operands: ["shd2", 1080000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "dy1", fmla: { operation: FP.Sine, operands: ["shd2", 18] } },
		// { name: "dy2", fmla: { operation: FP.Sine, operands: ["shd2", 18360000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "dy2", fmla: { operation: FP.Sine, operands: ["shd2", 306] } },
		{ name: "x1", fmla: { operation: FP.AddSubtract, operands: ["hc", 0, "dx1"] } },
		{ name: "x2", fmla: { operation: FP.AddSubtract, operands: ["hc", 0, "dx2"] } },
		{ name: "x3", fmla: { operation: FP.AddSubtract, operands: ["hc", "dx2", 0] } },
		{ name: "x4", fmla: { operation: FP.AddSubtract, operands: ["hc", "dx1", 0] } },
		{ name: "y1", fmla: { operation: FP.AddSubtract, operands: ["svc", 0, "dy1"] } },
		{ name: "y2", fmla: { operation: FP.AddSubtract, operands: ["svc", 0, "dy2"] } },
		{ name: "iwd2", fmla: { operation: FP.MultiplyDivide, operands: ["swd2", "a", 50000] } },
		{ name: "ihd2", fmla: { operation: FP.MultiplyDivide, operands: ["shd2", "a", 50000] } },
		// { name: "sdx1", fmla: { operation: FP.Cosine, operands: ["iwd2", 20520000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "sdx1", fmla: { operation: FP.Cosine, operands: ["iwd2", 342] } },
		// { name: "sdx2", fmla: { operation: FP.Cosine, operands: ["iwd2", 3240000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "sdx2", fmla: { operation: FP.Cosine, operands: ["iwd2", 54] } },
		// { name: "sdy1", fmla: { operation: FP.Sine, operands: ["ihd2", 3240000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "sdy1", fmla: { operation: FP.Sine, operands: ["ihd2", 54] } },
		// { name: "sdy2", fmla: { operation: FP.Sine, operands: ["ihd2", 20520000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "sdy2", fmla: { operation: FP.Sine, operands: ["ihd2", 342] } },
		{ name: "sx1", fmla: { operation: FP.AddSubtract, operands: ["hc", 0, "sdx1"] } },
		{ name: "sx2", fmla: { operation: FP.AddSubtract, operands: ["hc", 0, "sdx2"] } },
		{ name: "sx3", fmla: { operation: FP.AddSubtract, operands: ["hc", "sdx2", 0] } },
		{ name: "sx4", fmla: { operation: FP.AddSubtract, operands: ["hc", "sdx1", 0] } },
		{ name: "sy1", fmla: { operation: FP.AddSubtract, operands: ["svc", 0, "sdy1"] } },
		{ name: "sy2", fmla: { operation: FP.AddSubtract, operands: ["svc", 0, "sdy2"] } },
		{ name: "sy3", fmla: { operation: FP.AddSubtract, operands: ["svc", "ihd2", 0] } },
		{ name: "yAdj", fmla: { operation: FP.AddSubtract, operands: ["svc", 0, "ihd2"] } },
	],

	pathLst: [{
		operations: [
			{ pt: { x: "x1", y: "y1" }, type: PathOperationTypes.MOVE_TO },
			{ pt: { x: "sx2", y: "sy1" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "hc", y: "t" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "sx3", y: "sy1" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "x4", y: "y1" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "sx4", y: "sy2" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "x3", y: "y2" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "hc", y: "sy3" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "x2", y: "y2" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "sx1", y: "sy2" }, type: PathOperationTypes.LINE_TO },
			{ type: PathOperationTypes.CLOSE },
		],
	}],

	presetShape: ShapeType.star5,
	rect: { l: "sx1", t: "sy1", r: "sx4", b: "sy3" },
	type: GeometryTypes.PRESET,
};

/**
  * Microsoft open xml preset definition (for Reference purpose only)
  * <star5>
  * <avLst xmlns="http:  *schemas.openxmlformats.org/drawingml/2006/main">
  *   <gd name="adj" fmla="val 19098" />
  *   <gd name="hf" fmla="val 105146" />
  *   <gd name="vf" fmla="val 110557" />
  * </avLst>
  * <gdLst xmlns="http:  *schemas.openxmlformats.org/drawingml/2006/main">
  *   <gd name="a" fmla="pin 0 adj 50000" />
  *   <gd name="swd2" fmla=" * / wd2 hf 100000" />
  *   <gd name="shd2" fmla=" * / hd2 vf 100000" />
  *   <gd name="svc" fmla=" * / vc  vf 100000" />
  *   <gd name="dx1" fmla="cos swd2 1080000" />
  *   <gd name="dx2" fmla="cos swd2 18360000" />
  *   <gd name="dy1" fmla="sin shd2 1080000" />
  *   <gd name="dy2" fmla="sin shd2 18360000" />
  *   <gd name="x1" fmla="+- hc 0 dx1" />
  *   <gd name="x2" fmla="+- hc 0 dx2" />
  *   <gd name="x3" fmla="+- hc dx2 0" />
  *   <gd name="x4" fmla="+- hc dx1 0" />
  *   <gd name="y1" fmla="+- svc 0 dy1" />
  *   <gd name="y2" fmla="+- svc 0 dy2" />
  *   <gd name="iwd2" fmla=" * / swd2 a 50000" />
  *   <gd name="ihd2" fmla=" * / shd2 a 50000" />
  *   <gd name="sdx1" fmla="cos iwd2 20520000" />
  *   <gd name="sdx2" fmla="cos iwd2 3240000" />
  *   <gd name="sdy1" fmla="sin ihd2 3240000" />
  *   <gd name="sdy2" fmla="sin ihd2 20520000" />
  *   <gd name="sx1" fmla="+- hc 0 sdx1" />
  *   <gd name="sx2" fmla="+- hc 0 sdx2" />
  *   <gd name="sx3" fmla="+- hc sdx2 0" />
  *   <gd name="sx4" fmla="+- hc sdx1 0" />
  *   <gd name="sy1" fmla="+- svc 0 sdy1" />
  *   <gd name="sy2" fmla="+- svc 0 sdy2" />
  *   <gd name="sy3" fmla="+- svc ihd2 0" />
  *   <gd name="yAdj" fmla="+- svc 0 ihd2" />
  * </gdLst>
  * <ahLst xmlns="http:  *schemas.openxmlformats.org/drawingml/2006/main">
  *   <ahXY gdRefY="adj" minY="0" maxY="50000">
  *     <pos x="hc" y="yAdj" />
  *   </ahXY>
  * </ahLst>
  * <cxnLst xmlns="http:  *schemas.openxmlformats.org/drawingml/2006/main">
  *   <cxn ang="3cd4">
  *     <pos x="hc" y="t" />
  *   </cxn>
  *   <cxn ang="cd2">
  *     <pos x="x1" y="y1" />
  *   </cxn>
  *   <cxn ang="cd4">
  *     <pos x="x2" y="y2" />
  *   </cxn>
  *   <cxn ang="cd4">
  *     <pos x="x3" y="y2" />
  *   </cxn>
  *   <cxn ang="0">
  *     <pos x="x4" y="y1" />
  *   </cxn>
  * </cxnLst>
  * <rect l="sx1" t="sy1" r="sx4" b="sy3" xmlns="http:  *schemas.openxmlformats.org/drawingml/2006/main" />
  * <pathLst xmlns="http:  *schemas.openxmlformats.org/drawingml/2006/main">
  *   <path>
  *     <moveTo>
  *       <pt x="x1" y="y1" />
  *     </moveTo>
  *     <lnTo>
  *       <pt x="sx2" y="sy1" />
  *     </lnTo>
  *     <lnTo>
  *       <pt x="hc" y="t" />
  *     </lnTo>
  *     <lnTo>
  *       <pt x="sx3" y="sy1" />
  *     </lnTo>
  *     <lnTo>
  *       <pt x="x4" y="y1" />
  *     </lnTo>
  *     <lnTo>
  *       <pt x="sx4" y="sy2" />
  *     </lnTo>
  *     <lnTo>
  *       <pt x="x3" y="y2" />
  *     </lnTo>
  *     <lnTo>
  *       <pt x="hc" y="sy3" />
  *     </lnTo>
  *     <lnTo>
  *       <pt x="x2" y="y2" />
  *     </lnTo>
  *     <lnTo>
  *       <pt x="sx1" y="sy2" />
  *     </lnTo>
  *     <close />
  *   </path>
  * </pathLst>
  * </star5>
  */
